import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';
import Tooltip from '../components/Tooltip';

const VERDE = '#1F6A2D';

const brl = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataFmt = d => {
  if (!d) return '-';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const [y, m, day] = s.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
};

const STATUS = {
  PENDENTE: { label: 'Pendente', cls: 'badge-pendente' },
  APROVADO: { label: 'Aprovado', cls: 'badge-filial' },
  RECUSADO: { label: 'Recusado', cls: 'badge-negativo' },
  CANCELADO: { label: 'Cancelado', cls: 'badge-neutro' },
  PAGO: { label: 'Pago', cls: 'badge-positivo' },
};

const TIPOS_PIX = [
  { value: 'CPF_CNPJ', label: 'CPF/CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'TELEFONE', label: 'Telefone' },
  { value: 'ALEATORIA', label: 'Chave aleatória' },
];

const FORM_INICIAL = {
  tipo_chave_pix: 'CPF_CNPJ',
  chave_pix: '',
  observacao: '',
};

function StatusBadge({ status }) {
  const cfg = STATUS[status] || { label: status, cls: 'badge-neutro' };
  return <span className={cfg.cls}>{cfg.label}</span>;
}

// Faz download autenticado do comprovante (axios injeta o Bearer token)
async function baixarComprovante(solicitacaoId) {
  try {
    const r = await api.get(`/saques/${solicitacaoId}/comprovante`, { responseType: 'blob' });
    const cd = r.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/i);
    const nome = m?.[1] || `comprovante-${solicitacaoId}`;
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Não foi possível baixar o comprovante', confirmButtonColor: VERDE });
  }
}

export default function SolicitarSaque() {
  const { getToken } = useAuth();
  const [dados, setDados] = useState(null);
  const [historicoRecebido, setHistoricoRecebido] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [form, setForm] = useState(FORM_INICIAL);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  void getToken;

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro('');
    Promise.all([
      api.get('/saques/minhas'),
      api.get('/comissoes/historico-pagamentos'),
    ])
      .then(([s, h]) => {
        setDados(s.data);
        setHistoricoRecebido(h.data || []);
        setSelecionadas([]);
      })
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar dados de saque'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const comissoes       = dados?.comissoes_disponiveis || [];
  const saldoSolicitavel = dados?.saldo_solicitavel || 0;
  const estornosAbertos  = dados?.estornos_abertos  || [];
  const valorEstornos    = Number(dados?.valor_estornos || 0);        // negativo ou 0
  const saldoBruto       = Number(dados?.saldo_bruto    || saldoSolicitavel);

  const idsSelecionados = useMemo(() => new Set(selecionadas), [selecionadas]);
  const valorSelecionado = useMemo(
    () => comissoes
      .filter(c => idsSelecionados.has(c.conta_oid))
      .reduce((acc, c) => acc + Number(c.credito || 0), 0),
    [comissoes, idsSelecionados]
  );
  // Valor líquido do saque = selecionado + estornos (que é negativo)
  const valorLiquido = valorSelecionado + valorEstornos;

  const todasSelecionadas = comissoes.length > 0 && selecionadas.length === comissoes.length;
  // Com estornos pendentes, só libera o botão quando o valor líquido é positivo
  const podeSolicitar =
    selecionadas.length > 0 && !salvando &&
    (valorEstornos >= 0 || valorLiquido > 0);

  const alternarComissao = (contaOid) => {
    setSelecionadas(ids => ids.includes(contaOid)
      ? ids.filter(id => id !== contaOid)
      : [...ids, contaOid]);
  };

  const alternarTodas = () => {
    setSelecionadas(todasSelecionadas ? [] : comissoes.map(c => c.conta_oid));
  };

  const solicitar = async (e) => {
    e.preventDefault();
    if (salvando) return;

    if (!selecionadas.length) {
      Swal.fire({ icon: 'warning', title: 'Selecione ao menos uma comissão', confirmButtonColor: VERDE });
      return;
    }

    if (!form.chave_pix.trim()) {
      Swal.fire({ icon: 'warning', title: 'Informe a chave PIX', confirmButtonColor: VERDE });
      return;
    }

    // Com estornos pendentes, o saque líquido pode ficar <= 0 — bloqueia e orienta
    if (valorEstornos < 0 && valorLiquido <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Selecione mais comissões',
        html: `Você tem <b>${brl(-valorEstornos)}</b> em estornos pendentes que serão descontados. ` +
              `O valor selecionado (<b>${brl(valorSelecionado)}</b>) não cobre esse desconto.`,
        confirmButtonColor: VERDE,
      });
      return;
    }

    // Confirmação com breakdown quando há estornos a descontar
    if (valorEstornos < 0) {
      const { isConfirmed } = await Swal.fire({
        icon: 'info',
        title: 'Confirmar solicitação',
        html: `
          <div style="text-align:left; font-size:0.95rem; line-height:1.6;">
            <div>Comissões selecionadas: <b>${brl(valorSelecionado)}</b></div>
            <div style="color:#dc3545;">Estornos pendentes: <b>${brl(valorEstornos)}</b></div>
            <hr style="margin:8px 0;" />
            <div>Valor a receber via PIX: <b style="color:${VERDE};">${brl(valorLiquido)}</b></div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Solicitar saque',
        cancelButtonText: 'Voltar',
        confirmButtonColor: VERDE,
      });
      if (!isConfirmed) return;
    }

    setSalvando(true);
    try {
      await api.post('/saques', {
        ...form,
        conta_oids: selecionadas,
      });
      setForm(FORM_INICIAL);
      await Swal.fire({ icon: 'success', title: 'Solicitação enviada', timer: 1500, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Erro ao solicitar saque', confirmButtonColor: VERDE });
    } finally {
      setSalvando(false);
    }
  };

  const cancelar = async (id) => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: 'Cancelar solicitação?',
      text: 'As comissões selecionadas voltarão a ficar disponíveis para solicitação.',
      showCancelButton: true,
      confirmButtonText: 'Cancelar solicitação',
      cancelButtonText: 'Voltar',
      confirmButtonColor: VERDE,
    });

    if (!isConfirmed) return;

    try {
      await api.patch(`/saques/${id}/cancelar`);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Erro ao cancelar solicitação', confirmButtonColor: VERDE });
    }
  };

  return (
    <div>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-bank me-2" style={{ color: 'var(--primary)' }}></i>
        Solicitar Saque
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
        Selecione as comissões que deseja sacar via PIX.
      </p>

      {carregando ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
        </div>
      ) : erro ? (
        <div className="alert alert-danger">{erro}</div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <div className="card card-stat p-3" style={{ borderLeftColor: 'var(--primary)' }}>
                <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Disponível para Saque</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary)', marginTop: 4 }}>{brl(dados?.disponivel_saque)}</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card card-stat p-3" style={{ borderLeftColor: 'var(--accent)' }} title="Valor de solicitações de saque já enviadas, aguardando o pagamento PIX">
                <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aguardando pagamento</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--accent)', marginTop: 4 }}>{brl(dados?.valor_reservado)}</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card card-stat p-3" style={{ borderLeftColor: 'var(--success)' }}>
                <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {valorEstornos < 0 ? 'Líquido a receber' : 'Selecionado'}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--success)', marginTop: 4 }}>
                  {brl(valorEstornos < 0 ? valorLiquido : valorSelecionado)}
                </div>
                {valorEstornos < 0 && selecionadas.length > 0 && (
                  <div className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                    {brl(valorSelecionado)} − {brl(-valorEstornos)} de estornos
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Aviso de estornos pendentes — mostra o débito que será abatido do saque */}
          {estornosAbertos.length > 0 && (
            <div
              className="mb-4"
              style={{
                background: '#FDECEA',
                border: '1px solid #F5B5B2',
                borderLeft: '4px solid #dc3545',
                borderRadius: 'var(--raio, 12px)',
                padding: '12px 16px',
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ color: '#dc3545', fontSize: '1.15rem', marginTop: 1, flexShrink: 0 }} />
                <div style={{ fontSize: '0.88rem', color: '#5c1a16', lineHeight: 1.5, flex: 1 }}>
                  <div>
                    <strong>Estornos pendentes:</strong> você tem <b>{brl(-valorEstornos)}</b> em estornos de
                    comissão em aberto no sistema ({estornosAbertos.length} lançamento{estornosAbertos.length > 1 ? 's' : ''}).
                    Esse valor será descontado automaticamente do próximo saque solicitado.
                  </div>
                  {estornosAbertos.length <= 6 && (
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20, fontSize: '0.82rem' }}>
                      {estornosAbertos.map(e => (
                        <li key={e.conta_oid}>
                          <span style={{ fontFamily: 'monospace' }}>{e.codigo}</span>
                          {' · '}{dataFmt(e.emissao)}{' · '}
                          <b>{brl(e.valor)}</b>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Histórico de pagamentos já recebidos (transparência: o portal mostra
              tudo que veio do ERP — PIX, dinheiro, uso como crédito na loja, etc.
              O canal vem da forma de pagamento real registrada no ERP, sem suposição) */}
          {historicoRecebido.length > 0 && (
            <HistoricoRecebido itens={historicoRecebido} />
          )}

          <form onSubmit={solicitar}>
            <div className="card p-0 mb-4">
              <div className="p-3 border-bottom bg-white d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <h6 className="fw-bold mb-0">Suas comissões</h6>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={alternarTodas} disabled={!comissoes.length}>
                  {todasSelecionadas ? 'Limpar seleção' : 'Selecionar todas'}
                </button>
              </div>
              <div className="tabela-container">
                <table className="table table-hover table-arq mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}></th>
                      <th>Emissão</th>
                      <th>Código</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comissoes.length ? comissoes.map(c => (
                      <tr key={c.conta_oid} className="linha-clicavel" onClick={() => alternarComissao(c.conta_oid)}>
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={idsSelecionados.has(c.conta_oid)}
                            onChange={() => alternarComissao(c.conta_oid)}
                          />
                        </td>
                        <td className="text-muted">{dataFmt(c.emissao)}</td>
                        <td><span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#555' }}>{c.codigo}</span></td>
                        <td className="text-end fw-semibold">{brl(c.credito)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4" style={{ padding: 0 }}>
                          <EmptyState
                            icone="bi-bag-check"
                            titulo="Nenhuma comissão disponível"
                            texto="Quando alguém comprar no Centrão indicado por você, sua comissão aparece aqui pra sacar via PIX."
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {comissoes.length > 0 && (
                    <tfoot>
                      {valorEstornos < 0 && (
                        <>
                          <tr style={{ background: '#f8f9fa', fontWeight: 600, color: '#555' }}>
                            <td colSpan="3">Saldo bruto</td>
                            <td className="text-end">{brl(saldoBruto)}</td>
                          </tr>
                          <tr style={{ background: '#f8f9fa', color: '#dc3545' }}>
                            <td colSpan="3">Estornos pendentes</td>
                            <td className="text-end">{brl(valorEstornos)}</td>
                          </tr>
                        </>
                      )}
                      <tr style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
                        <td colSpan="3">Saldo solicitável</td>
                        <td className="text-end">{brl(saldoSolicitavel)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            <div className="card p-4 mb-4">
              <div className="grid-formulario">
                <div>
                  <label className="form-label fw-semibold">Tipo de chave PIX</label>
                  <select
                    className="form-select"
                    value={form.tipo_chave_pix}
                    onChange={e => setForm(f => ({ ...f, tipo_chave_pix: e.target.value }))}
                    disabled={!comissoes.length}
                  >
                    {TIPOS_PIX.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="form-label fw-semibold">Chave PIX</label>
                  <input
                    className="form-control"
                    maxLength={200}
                    value={form.chave_pix}
                    onChange={e => setForm(f => ({ ...f, chave_pix: e.target.value }))}
                    disabled={!comissoes.length}
                  />
                </div>

                <div>
                  <label className="form-label fw-semibold">Observação <span className="text-muted fw-normal">({form.observacao.length}/500)</span></label>
                  <input
                    className="form-control"
                    maxLength={500}
                    value={form.observacao}
                    onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))}
                    disabled={!comissoes.length}
                  />
                </div>
              </div>

              <div className="mt-4">
                <button type="submit" className="btn-primary-arq" disabled={!podeSolicitar}>
                  {salvando
                    ? 'Enviando...'
                    : <><i className="bi bi-send me-1"></i>Solicitar {brl(valorEstornos < 0 ? valorLiquido : valorSelecionado)}</>}
                </button>
                {valorEstornos < 0 && selecionadas.length > 0 && valorLiquido <= 0 && (
                  <div className="text-danger mt-2" style={{ fontSize: '0.82rem' }}>
                    <i className="bi bi-exclamation-circle me-1" />
                    Selecione comissões suficientes para cobrir os {brl(-valorEstornos)} em estornos pendentes.
                  </div>
                )}
              </div>
            </div>
          </form>

          <div className="card p-0">
            <div className="p-3 border-bottom bg-white">
              <h6 className="fw-bold mb-0">Minhas solicitações</h6>
            </div>
            <div className="tabela-container">
              <table className="table table-hover table-arq mb-0">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th className="text-end">Valor</th>
                    <th className="text-center">Itens</th>
                    <th>PIX</th>
                    <th>Status</th>
                    <th>Resposta</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {dados?.solicitacoes?.length ? dados.solicitacoes.map(s => (
                    <tr key={s.id}>
                      <td className="text-muted">{dataFmt(s.criado_em)}</td>
                      <td className="text-end fw-semibold">{brl(s.valor_solicitado)}</td>
                      <td className="text-center text-muted">{s.qtd_itens}</td>
                      <td>
                        <div className="fw-semibold">{TIPOS_PIX.find(t => t.value === s.tipo_chave_pix)?.label || s.tipo_chave_pix}</div>
                        <div className="text-muted" style={{ fontSize: '0.85rem', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {s.chave_pix}
                        </div>
                      </td>
                      <td><StatusBadge status={s.status} /></td>
                      <Tooltip texto={s.resposta || null}>
                        <td className="text-muted" style={{ fontSize: '0.88rem', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: s.resposta ? 'help' : 'default' }}>
                          {s.resposta || '-'}
                        </td>
                      </Tooltip>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end align-items-center">
                          {s.tem_comprovante ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => baixarComprovante(s.id)}
                              title="Baixar comprovante PIX"
                            >
                              <i className="bi bi-download me-1"></i>Comprovante
                            </button>
                          ) : null}
                          {s.status === 'PENDENTE' && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => cancelar(s.id)}>
                              Cancelar
                            </button>
                          )}
                          {!s.tem_comprovante && s.status !== 'PENDENTE' && (
                            <span className="text-muted">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" style={{ padding: 0 }}>
                        <EmptyState
                          icone="bi-bank"
                          titulo="Você ainda não fez nenhuma solicitação"
                          texto="Selecione comissões na lista acima e clique em Solicitar."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// HistoricoRecebido — mostra TUDO que o arquiteto já recebeu, por canal
// (PIX do portal, dinheiro, uso como crédito na loja, estorno por devolução).
// O canal vem da forma de pagamento REAL registrada no ERP — não supomos que
// baixa na loja foi "dinheiro" (pode ter sido crédito usado em compra).
// Transparência radical: o arquiteto vê exatamente o que o ERP registra.
// =============================================================================
const CANAL_LABEL = {
  pix:        { rotulo: 'PIX',                 cor: '#1F6A2D', bg: '#E6F5EA', icone: 'bi-bank2' },
  dinheiro:   { rotulo: 'Dinheiro',            cor: '#1F6A2D', bg: '#F0F4EF', icone: 'bi-cash-stack' },
  abatimento: { rotulo: 'Usado como crédito',  cor: '#C8860D', bg: '#FBEFD6', icone: 'bi-arrow-down-up' },
  estorno:    { rotulo: 'Estorno (devolução)', cor: '#B53D3D', bg: '#FDECEA', icone: 'bi-arrow-counterclockwise' },
  outro:      { rotulo: 'Outro',               cor: '#6F6A63', bg: '#F4F6F7', icone: 'bi-three-dots' },
};

function HistoricoRecebido({ itens }) {
  // Agrega por canal pra mostrar resumo no topo
  const resumo = itens.reduce((acc, i) => {
    const c = i.canal || 'outro';
    if (!acc[c]) acc[c] = { qtd: 0, total: 0 };
    acc[c].qtd += 1;
    acc[c].total += Number(i.valor || 0);
    return acc;
  }, {});

  const canaisOrdenados = Object.entries(resumo).sort((a, b) => Math.abs(b[1].total) - Math.abs(a[1].total));
  const [aberto, setAberto] = useState(false);
  const totalGeral = itens.reduce((s, i) => s + Number(i.valor || 0), 0);

  return (
    <div className="card mb-4" style={{ borderLeft: '4px solid var(--primary)' }}>
      <div
        className="p-3 d-flex align-items-center justify-content-between gap-3 flex-wrap"
        onClick={() => setAberto(v => !v)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        role="button"
      >
        <div>
          <h6 className="fw-bold mb-1">
            <i className="bi bi-receipt me-2" style={{ color: 'var(--primary)' }}></i>
            O que você já recebeu
          </h6>
          <div className="text-muted" style={{ fontSize: '0.82rem' }}>
            Total: <strong style={{ color: 'var(--primary-dark)' }}>{brl(totalGeral)}</strong> em {itens.length} comissões.
            {' '}Mostra também o que já foi baixado fora do portal (não só PIX).
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {canaisOrdenados.slice(0, 3).map(([canal, info]) => {
            const cfg = CANAL_LABEL[canal] || CANAL_LABEL.outro;
            return (
              <span key={canal}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: cfg.bg, color: cfg.cor,
                  padding: '4px 10px', borderRadius: 14,
                  fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
                }}>
                <i className={`bi ${cfg.icone}`} />
                {cfg.rotulo}: {brl(info.total)}
              </span>
            );
          })}
          <i className={`bi ${aberto ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
        </div>
      </div>

      {aberto && (
        <div className="tabela-container" style={{ borderTop: '1px solid var(--borda)' }}>
          <table className="table table-hover table-arq mb-0">
            <thead>
              <tr>
                <th>Data pgto.</th>
                <th>Pedido</th>
                <th className="text-end">Valor</th>
                <th>Canal</th>
                <th>Caixa / Forma</th>
              </tr>
            </thead>
            <tbody>
              {itens.map(it => {
                const cfg = CANAL_LABEL[it.canal] || CANAL_LABEL.outro;
                const negativo = it.canal === 'estorno' || Number(it.valor) < 0;
                return (
                  <tr key={it.conta_oid}>
                    <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{dataFmt(it.pagamento)}</td>
                    <td><span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#555' }}>{it.codigo}</span></td>
                    <td className="text-end fw-semibold" style={{
                      color: negativo ? '#B53D3D' : 'inherit', whiteSpace: 'nowrap',
                    }}>
                      {negativo ? '- ' : ''}{brl(Math.abs(Number(it.valor || 0)))}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: cfg.bg, color: cfg.cor,
                        padding: '2px 8px', borderRadius: 12,
                        fontSize: '0.78rem', fontWeight: 600,
                      }}>
                        <i className={`bi ${cfg.icone}`} />{cfg.rotulo}
                      </span>
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      {it.caixa || it.forma_pagamento || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
